import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RecyclingIcon from "@mui/icons-material/Recycling";
import VerifiedIcon from "@mui/icons-material/Verified";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { PagePaper } from "@/components/Paper";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import type { CorporateAttributes } from "../types";
import { byPeriodAsc, toStrList } from "../utils/normalize";
import { formatNumberOrDash, formatYenCompact } from "../utils/format";
import { buildAcquireUrl, corporateMasterListPathFrom } from "../utils/paths";
import { canDownloadPdf, useCommercialPdfDownload } from "../utils/download";
import { detailCompactSx } from "../utils/styles";
import { NO_CORPORATE_NAME_LABEL, PUBLIC_REGISTRY_LABEL } from "../constants";
import { useCorporateMasterDetail } from "../hooks/useCorporateMasterDetail";
import { HeroStat } from "../components/HeroStat";
import { BasicInfoTab } from "../components/BasicInfoTab";
import { AttributesTab } from "../components/AttributesTab";
import { ListedInfoTab } from "../components/ListedInfoTab";
import { CommercialAnalysisTab } from "../components/CommercialAnalysisTab";
import { ReceptionBooksTab } from "../components/ReceptionBooksTab";
import { KanpoNoticesTab } from "../components/KanpoNoticesTab";
import { useFeatureFlags } from "@/configs/featureFlag";
import { PublicRegistryTab } from "../components/PublicRegistryTab";
import { RegistryTagChips } from "../components/registryTags/RegistryTagChips";

const baseLabels = [
  "基本情報",
  "属性・業績",
  "有報情報",
  "商業登記サマリ",
  "商業登記受付帳",
  PUBLIC_REGISTRY_LABEL,
];
// 官報タブは、公告のデータが入るまで Launch Darkly のフラグで隠す
const KANPO_TAB_INDEX = baseLabels.length;
const KANPO_TAB_LABEL = "官報";

// 最新決算期の売上高を取る。最新期の売上が空なら空のまま返し、前期以前の売上で置き換えない
// （一覧・売上検索の BE 判定と揃える）。gBiz の period は「何期前」(0=当期)のため、
// BE の periodOffset を正として判定する。
const latestNetSales = (
  indexes: CorporateAttributes["financeIndexes"]
): number | null => [...indexes].sort(byPeriodAsc).at(-1)?.netSales ?? null;

export const CorporateMasterDetail: React.FC = () => {
  const { corporateNumber = "" } = useParams();
  const location = useLocation();
  const { data, isLoading, error, mutate } =
    useCorporateMasterDetail(corporateNumber);
  const [tab, setTab] = useState(0);
  const { corporateMasterKanpoTab } = useFeatureFlags();
  const labels = corporateMasterKanpoTab
    ? [...baseLabels, KANPO_TAB_LABEL]
    : baseLabels;
  // 別法人の詳細へ遷移したら先頭タブ（基本情報）に戻す。
  useEffect(() => {
    setTab(0);
  }, [corporateNumber]);
  // フラグがオフになって官報タブが消えたとき、開いていたタブの番号が範囲の外にならないよう先頭に戻す。
  useEffect(() => {
    if (tab >= labels.length) setTab(0);
  }, [tab, labels.length]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // 閲覧専用ユーザーは取得できないため、取得の導線を押せないようにする（既存の商業登記画面と同じ）
  const isReadOnly = useReadOnlyUser();
  const valid = /^\d{13}$/.test(corporateNumber);
  // 商業登記取得（commercialbook）への遷移先。SPでは法人名リンクに集約する。
  const acquireUrl = data
    ? buildAcquireUrl(data.basic.corporateNumberLast12, data.basic.name)
    : "";
  const compactSx = isMobile ? detailCompactSx : undefined;
  const industries = toStrList(data?.attributes?.industry);
  // 取得済みの場合にダウンロードできるPDF（成功した取得済み商業登記の先頭）。URLは押下時に発行する。
  const acquiredPdf = data?.commercialAnalysis.find(canDownloadPdf) ?? null;
  const downloadPdf = useCommercialPdfDownload(corporateNumber);
  return (
    <PagePaper>
      <Stack spacing={3} sx={compactSx}>
        <Link
          component={RouterLink}
          to={corporateMasterListPathFrom(location.state)}
        >
          法人情報一覧へ
        </Link>
        {!valid ? (
          <Alert severity="warning">
            法人番号は数字13桁で指定してください。
          </Alert>
        ) : isLoading ? (
          <CircularProgress aria-label="法人情報を読み込み中" />
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button
                onClick={() => {
                  void mutate();
                }}
              >
                再試行
              </Button>
            }
          >
            法人情報の取得に失敗しました。
          </Alert>
        ) : !data ? (
          <Alert severity="info">
            法人が見つかりません。一覧から法人を選んでください。
          </Alert>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 3, boxShadow: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                >
                  {isMobile && !isReadOnly ? (
                    <Link
                      component={RouterLink}
                      to={acquireUrl}
                      variant="h5"
                      underline="hover"
                      sx={{
                        fontWeight: "bold",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {data.basic.name ?? NO_CORPORATE_NAME_LABEL}
                      <ArrowForwardIcon fontSize="small" />
                    </Link>
                  ) : (
                    <Typography variant="h5" component="h1">
                      {data.basic.name ?? NO_CORPORATE_NAME_LABEL}
                    </Typography>
                  )}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ flexShrink: 0 }}
                  >
                    {data.reuseAvailable && !data.hasAcquired ? (
                      <ReadOnlyTooltip isReadOnly={isReadOnly}>
                        <Button
                          component={RouterLink}
                          to={buildAcquireUrl(
                            data.basic.corporateNumberLast12,
                            data.basic.name,
                            {
                              reuse: true,
                              returnCorporateNumber: corporateNumber,
                            }
                          )}
                          variant="outlined"
                          startIcon={<RecyclingIcon />}
                          sx={{ whiteSpace: "nowrap" }}
                          disabled={isReadOnly}
                        >
                          リユースで取得
                        </Button>
                      </ReadOnlyTooltip>
                    ) : null}
                    {!isMobile ? (
                      <ReadOnlyTooltip isReadOnly={isReadOnly}>
                        <Button
                          component={RouterLink}
                          to={acquireUrl}
                          variant="outlined"
                          endIcon={<ArrowForwardIcon />}
                          sx={{ whiteSpace: "nowrap" }}
                          disabled={isReadOnly}
                        >
                          商業登記を取得
                        </Button>
                      </ReadOnlyTooltip>
                    ) : null}
                  </Stack>
                </Stack>
                <Stack spacing={0.25}>
                  <Typography>
                    法人番号：{data.basic.corporateNumber}
                  </Typography>
                  <Typography>
                    本店所在地：
                    {[
                      data.basic.prefectureName,
                      data.basic.cityName,
                      data.basic.streetNumber,
                    ]
                      .filter(Boolean)
                      .join("") || "—"}
                  </Typography>
                  {data.attributes?.representativeName ? (
                    <Typography>
                      代表者：{data.attributes.representativeName}
                    </Typography>
                  ) : null}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {data.listed ? (
                    <Chip
                      size="small"
                      color="primary"
                      label="上場"
                      sx={{ borderRadius: 0.5 }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={data.attributes ? "非上場" : "情報未取得"}
                      sx={{ borderRadius: 0.5 }}
                    />
                  )}
                  {data.listed?.marketSegment ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={data.listed.marketSegment}
                      sx={{ borderRadius: 0.5 }}
                    />
                  ) : null}
                  <Chip
                    size="small"
                    color="default"
                    variant="outlined"
                    label={data.basic.kindLabel ?? "法人種別未取得"}
                    sx={{ borderRadius: 0.5 }}
                  />
                </Stack>
                <RegistryTagChips tags={data.registryTags} />
                {data.attributes ? (
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      pt: 1,
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        sm: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <HeroStat
                      label="資本金"
                      value={formatYenCompact(data.attributes.capitalStock)}
                    />
                    <HeroStat
                      label="従業員"
                      value={formatNumberOrDash(
                        data.attributes.employeeNumber,
                        "人"
                      )}
                    />
                    <HeroStat
                      label="売上高"
                      value={formatYenCompact(
                        latestNetSales(data.attributes.financeIndexes)
                      )}
                    />
                    <HeroStat
                      label="設立"
                      value={
                        data.attributes.dateOfEstablishment
                          ? `${data.attributes.dateOfEstablishment.slice(
                              0,
                              4
                            )}年`
                          : "—"
                      }
                    />
                  </Box>
                ) : null}
                {industries.length ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {industries.slice(0, 5).map((ind) => (
                      <Chip
                        key={ind}
                        label={ind}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ borderRadius: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : null}
                {data.hasAcquired ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        pl: 1,
                        pr: 1.75,
                        py: 0.6,
                        borderRadius: 0.5,
                        bgcolor: "background.paper",
                        color: "text.primary",
                        border: 1,
                        borderColor: "grey.300",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      }}
                    >
                      <VerifiedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ letterSpacing: 0.3 }}
                      >
                        商業登記 取得済み
                      </Typography>
                    </Box>
                    {acquiredPdf ? (
                      <Tooltip title="PDFをダウンロード">
                        <IconButton
                          size="small"
                          aria-label="PDFをダウンロード"
                          onClick={() => {
                            void downloadPdf(acquiredPdf);
                          }}
                        >
                          <FileDownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
            {isMobile ? (
              <TextField
                select
                fullWidth
                size="small"
                label="表示する情報"
                value={tab}
                onChange={(event) => {
                  setTab(Number(event.target.value));
                }}
              >
                {labels.map((label, index) => (
                  <MenuItem key={label} value={index}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Tabs
                value={tab}
                onChange={(_event, value: number) => {
                  setTab(value);
                }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="法人情報の分類"
              >
                {labels.map((label, index) => (
                  <Tab
                    key={label}
                    label={label}
                    id={`corporate-tab-${index}`}
                    aria-controls={`corporate-panel-${index}`}
                  />
                ))}
              </Tabs>
            )}
            {labels.map((label, index) => (
              <Box
                key={label}
                role="tabpanel"
                hidden={tab !== index}
                id={`corporate-panel-${index}`}
                {...(isMobile
                  ? { "aria-label": label }
                  : { "aria-labelledby": `corporate-tab-${index}` })}
                tabIndex={0}
                sx={{ p: 2 }}
              >
                {tab === index ? (
                  <>
                    {index === 0 ? (
                      <BasicInfoTab
                        basic={data.basic}
                        attributes={data.attributes}
                      />
                    ) : null}
                    {index === 1 ? (
                      <AttributesTab attributes={data.attributes} />
                    ) : null}
                    {index === 2 ? (
                      <ListedInfoTab listed={data.listed} />
                    ) : null}
                    {index === 3 ? (
                      <CommercialAnalysisTab
                        corporateNumber={corporateNumber}
                        summaries={data.commercialAnalysis}
                        corporateNumberLast12={data.basic.corporateNumberLast12}
                        // 取得画面の会社名欄に入るため、表示用の「（法人名なし）」は渡さない
                        name={data.basic.name}
                        hasAcquired={data.hasAcquired}
                        reuseAvailable={data.reuseAvailable}
                      />
                    ) : null}
                    {index === 4 ? (
                      <ReceptionBooksTab
                        key={corporateNumber}
                        corporateNumber={data.basic.corporateNumber}
                      />
                    ) : null}
                    {index === 5 ? (
                      <PublicRegistryTab tags={data.registryTags} />
                    ) : null}
                    {index === KANPO_TAB_INDEX ? (
                      <KanpoNoticesTab
                        key={corporateNumber}
                        corporateNumber={data.basic.corporateNumber}
                      />
                    ) : null}
                  </>
                ) : null}
              </Box>
            ))}
          </>
        )}
      </Stack>
    </PagePaper>
  );
};
